import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { getAuth, verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';

const LOGO_URL = '/brand/twp-logo.png';
const BRAND_BROWN = '#8B4513';

/**
 * Handles Firebase Auth action links (password reset, email verification, etc.)
 * hosted on our own domain so password managers associate saved passwords with
 * twp-calendar-61a9c.web.app / production.thewindowplacellc.com instead of
 * Firebase's hosted firebaseapp.com domain.
 *
 * URL: /auth/action?mode=resetPassword&oobCode=...&apiKey=...&continueUrl=...
 *
 * Currently only resetPassword mode is implemented. Other modes (verifyEmail,
 * recoverEmail) can be added here when needed.
 */
export default function AuthActionPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const mode    = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');

  // Stages: verifying | reset | success | error
  const [stage, setStage]                 = useState('verifying');
  const [email, setEmail]                 = useState('');
  const [newPassword, setNewPassword]     = useState('');
  const [confirmPassword, setConfirmPwd]  = useState('');
  const [showPassword, setShowPassword]   = useState(false);
  const [error, setError]                 = useState('');
  const [submitting, setSubmitting]       = useState(false);

  useEffect(() => {
    if (mode !== 'resetPassword' || !oobCode) {
      setStage('error');
      setError('Invalid or missing reset link. Open the link from your password reset email, or request a new one from the login page.');
      return;
    }

    const auth = getAuth();
    verifyPasswordResetCode(auth, oobCode)
      .then((emailFromCode) => {
        setEmail(emailFromCode);
        setStage('reset');
      })
      .catch((err) => {
        console.error('verifyPasswordResetCode error:', err);
        setStage('error');
        if (err.code === 'auth/expired-action-code') {
          setError('This reset link has expired. Request a new one from the login page.');
        } else if (err.code === 'auth/invalid-action-code') {
          setError('This reset link is invalid or has already been used. Request a new one from the login page.');
        } else {
          setError('Could not verify this reset link. Request a new one or contact Noah.');
        }
      });
  }, [mode, oobCode]);

  /** Strong password policy — 12+ chars, mixed case, number, symbol. */
  function validatePassword(pwd) {
    if (pwd.length < 12)                                              return 'Password must be at least 12 characters.';
    if (!/[a-z]/.test(pwd))                                           return 'Password must contain a lowercase letter.';
    if (!/[A-Z]/.test(pwd))                                           return 'Password must contain an uppercase letter.';
    if (!/[0-9]/.test(pwd))                                           return 'Password must contain a number.';
    if (!/[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\/;'`~]/.test(pwd))         return 'Password must contain a symbol (e.g. ! @ # $ % & *).';
    return null;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    const validationError = validatePassword(newPassword);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSubmitting(true);
      const auth = getAuth();
      await confirmPasswordReset(auth, oobCode, newPassword);
      setStage('success');
    } catch (err) {
      console.error('confirmPasswordReset error:', err);
      if (err.code === 'auth/weak-password') {
        setError('Password is too weak. Use at least 12 characters with mixed case, numbers, and symbols.');
      } else if (err.code === 'auth/expired-action-code') {
        setError('This reset link has expired. Request a new one from the login page.');
        setStage('error');
      } else if (err.code === 'auth/invalid-action-code') {
        setError('This reset link is invalid or has already been used. Request a new one.');
        setStage('error');
      } else {
        setError('Could not reset password. Try again or contact Noah.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  const cardStyle = {
    width: '100%',
    maxWidth: '460px',
    padding: '40px',
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(20px)',
    borderRadius: '24px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    border: '1px solid rgba(255, 255, 255, 0.5)'
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #8B4513 0%, #A0522D 100%)',
      padding: '20px'
    }}>
      <div style={cardStyle}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <img
            src={LOGO_URL}
            alt="The Window Place LLC"
            style={{ maxWidth: '180px', marginBottom: '16px', filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))' }}
          />
          <h1 style={{
            color: BRAND_BROWN,
            margin: '0 0 8px 0',
            fontSize: '24px',
            fontWeight: '700',
            background: 'linear-gradient(135deg, #8B4513 0%, #A0522D 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            {stage === 'success' ? 'Password Reset' : 'Reset Your Password'}
          </h1>
          <p style={{ margin: 0, color: '#666', fontSize: '14px', fontWeight: '500' }}>
            {stage === 'verifying' && 'Verifying your reset link…'}
            {stage === 'reset' && `Set a new password for ${email}`}
            {stage === 'success' && 'Your password has been updated.'}
            {stage === 'error' && 'There was a problem with your reset link.'}
          </p>
        </div>

        {error && (
          <div style={{
            padding: '12px 16px',
            marginBottom: '20px',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '12px',
            color: '#dc2626',
            fontSize: '14px',
            fontWeight: '500',
            lineHeight: '1.5'
          }}>
            {error}
          </div>
        )}

        {stage === 'verifying' && (
          <div style={{ textAlign: 'center', padding: '20px 0', color: '#666' }}>
            Please wait…
          </div>
        )}

        {stage === 'reset' && (
          <form onSubmit={handleSubmit} autoComplete="on">
            {/* Hidden username field tells password managers which account this password is for */}
            <input
              type="email"
              name="username"
              value={email}
              autoComplete="username"
              readOnly
              style={{ display: 'none' }}
            />

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: '#2b2b2b' }}>
                New Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  minLength={12}
                  style={{
                    width: '100%',
                    padding: '14px 48px 14px 16px',
                    border: '2px solid #e5e5e5',
                    borderRadius: '12px',
                    fontSize: '16px',
                    fontFamily: 'inherit',
                    outline: 'none',
                    WebkitAppearance: 'none',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = BRAND_BROWN}
                  onBlur={(e) => e.target.style.borderColor = '#e5e5e5'}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '8px',
                    fontSize: '14px',
                    color: '#666'
                  }}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
              <p style={{ fontSize: '12px', color: '#999', margin: '6px 0 0 0' }}>
                At least 12 characters, with upper + lower case, a number, and a symbol.
              </p>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: '#2b2b2b' }}>
                Confirm New Password
              </label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPwd(e.target.value)}
                required
                autoComplete="new-password"
                minLength={12}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  border: '2px solid #e5e5e5',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontFamily: 'inherit',
                  outline: 'none',
                  WebkitAppearance: 'none',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = BRAND_BROWN}
                onBlur={(e) => e.target.style.borderColor = '#e5e5e5'}
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              style={{
                width: '100%',
                padding: '16px',
                background: submitting ? '#999' : `linear-gradient(135deg, ${BRAND_BROWN} 0%, #A0522D 100%)`,
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '700',
                cursor: submitting ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s',
                boxShadow: submitting ? 'none' : '0 4px 12px rgba(139, 69, 19, 0.3)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                WebkitAppearance: 'none'
              }}
            >
              {submitting ? 'Updating…' : 'Set New Password'}
            </button>
          </form>
        )}

        {stage === 'success' && (
          <>
            <div style={{
              padding: '14px 16px',
              marginBottom: '20px',
              background: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: '12px',
              color: '#15803d',
              fontSize: '14px',
              fontWeight: '500',
              lineHeight: '1.5'
            }}>
              Password updated successfully. You can now sign in with your new password.
            </div>
            <button
              type="button"
              onClick={() => navigate('/login')}
              style={{
                width: '100%',
                padding: '16px',
                background: `linear-gradient(135deg, ${BRAND_BROWN} 0%, #A0522D 100%)`,
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.3s',
                boxShadow: '0 4px 12px rgba(139, 69, 19, 0.3)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                WebkitAppearance: 'none'
              }}
            >
              Go to Sign In
            </button>
          </>
        )}

        {stage === 'error' && (
          <Link
            to="/login"
            style={{
              display: 'block',
              width: '100%',
              padding: '16px',
              background: `linear-gradient(135deg, ${BRAND_BROWN} 0%, #A0522D 100%)`,
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: '700',
              cursor: 'pointer',
              textAlign: 'center',
              textDecoration: 'none',
              boxShadow: '0 4px 12px rgba(139, 69, 19, 0.3)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              boxSizing: 'border-box'
            }}
          >
            Back to Sign In
          </Link>
        )}

        <div style={{
          marginTop: '24px',
          paddingTop: '24px',
          borderTop: '1px solid #e5e5e5',
          textAlign: 'center'
        }}>
          <p style={{ fontSize: '12px', color: '#666', margin: '0 0 12px 0' }}>
            Need help? Contact Noah or any manager at TWP
          </p>
          <p style={{ fontSize: '12px', color: '#999', margin: 0 }}>
            The Window Place LLC {new Date().getFullYear()}
          </p>
        </div>
      </div>
    </div>
  );
}
